import { CTS_REVIEW_LABEL_TEXT } from '@grc/shared';
import { useComplianceDashboard } from '@/api/hooks/useComplianceDashboard';
import { useReviewSummary } from '@/api/hooks/useCtsReview';
import { BandHeader, BandCards, HomeCard, BigStat, StatPair, MixBar, CardHint, DrillLink, BandError } from './HomeBandKit';

// Home Page BI — Compliance band (entitlement-gated 'compliance'). One summary call
// (/dashboards/compliance). Real state only: 0 greys out; unassigned is "not yet worked", not failing.

export function ComplianceHomeBand() {
  const { data, isLoading, isError, refetch } = useComplianceDashboard();
  const reviews = useReviewSummary();

  const header = (
    <BandHeader
      tab="#4A9E78"
      title="Compliance"
      chip="Compliance"
      chipTone="compliance"
      drillLabel="Compliance Dashboard →"
      drillPath="/dashboards/compliance"
    />
  );

  if (isError) {
    return (
      <section className="flex flex-col gap-[9px]" data-testid="home-band-compliance" aria-label="Compliance">
        {header}
        <BandError title="Compliance summary" onRetry={() => refetch()} />
      </section>
    );
  }

  const total = data?.total_controls ?? 0;
  const addressed = data?.controls_addressed ?? 0;
  const unassigned = data?.status_distribution.find((s) => s.status === 'unassigned')?.count ?? 0;
  const assignedOrStarted = Math.max(0, total - addressed - unassigned);
  const evidenceExpiring = data ? data.evidence.expired + data.evidence.expiring : 0;
  const exceptions = data?.open_exceptions_count ?? 0;
  const overdue = reviews.data?.by_label.overdue.controls ?? 0;
  const dueSoon = reviews.data?.by_label.due_soon.controls ?? 0;
  const reviewsInProgress = (reviews.data?.by_label.overdue.in_progress ?? 0) + (reviews.data?.by_label.due_soon.in_progress ?? 0);

  return (
    <section className="flex flex-col gap-[9px]" data-testid="home-band-compliance" aria-label="Compliance">
      {header}
      {isLoading ? (
        <BandCards><HomeCard cap="Loading"><CardHint>Loading…</CardHint></HomeCard></BandCards>
      ) : (
        <BandCards>
          <HomeCard cap="Readiness by framework">
            <div className="flex flex-col gap-[7px]">
              {(data?.frameworks ?? []).map((f) => (
                <div className="flex items-center gap-[9px]" key={f.framework_id}>
                  <span className="flex-[0_0_92px] min-w-0 truncate text-[11px] text-[#9FB3C8]">{f.display_name}</span>
                  <span className="flex h-[8px] flex-[1_1_auto] overflow-hidden rounded-[3px] bg-[#0D1626]">
                    <span
                      className="block h-full"
                      style={{ width: `${f.readiness_pct}%`, background: f.readiness_pct === 0 ? '#3A5580' : '#4A9E78' }}
                    />
                  </span>
                  <span className="flex-[0_0_32px] text-right text-[11px] tabular-nums text-[#B7C8DA]">{f.readiness_pct}%</span>
                </div>
              ))}
            </div>
            <CardHint>Weighted by controls in scope</CardHint>
            <DrillLink label="View frameworks →" path="/compliance" />
          </HomeCard>

          <HomeCard cap="Controls">
            <BigStat value={`${addressed} / ${total}`} unit="addressed" />
            <MixBar
              segments={[
                { label: 'Addressed', count: addressed, tone: '#4A9E78' },
                { label: 'Assigned or started', count: assignedOrStarted, tone: '#D7A94A' },
                { label: 'Unassigned', count: unassigned, tone: '#3A5580' },
              ]}
            />
            <CardHint>{unassigned} controls not yet assigned.</CardHint>
            <DrillLink label="View the control list →" path="/compliance" />
          </HomeCard>

          {/* ⚠ CAPR S5 — the Controls list, counted by the SERVER by review label (GET
              /compliance/workspace/review-summary: the list's own scope, rows and resolver). The band only
              renders the counts; it dates nothing. Passive: an indicator, never an alert. */}
          <HomeCard cap="Reviews">
            {reviews.isError ? (
              <CardHint>Review counts are unavailable.</CardHint>
            ) : (
              <>
                <StatPair
                  left={{ n: overdue, l: CTS_REVIEW_LABEL_TEXT.overdue.toLowerCase(), color: overdue > 0 ? '#F09E99' : '#5C7189' }}
                  right={{ n: dueSoon, l: CTS_REVIEW_LABEL_TEXT.due_soon.toLowerCase(), color: dueSoon > 0 ? '#D7A94A' : '#5C7189' }}
                />
                <CardHint>{reviewsInProgress} of these have an assessment in progress.</CardHint>
              </>
            )}
            <DrillLink label="View the control list →" path="/compliance/workspace/controls" />
          </HomeCard>

          <HomeCard cap="Requires attention">
            <StatPair
              left={{ n: exceptions, l: 'open exceptions', color: exceptions > 0 ? '#F09E99' : '#5C7189' }}
              right={{ n: evidenceExpiring, l: 'evidence expiring', color: evidenceExpiring > 0 ? '#F09E99' : '#5C7189' }}
            />
            <DrillLink label="Review evidence →" path="/compliance/workspace/tasks?tab=evidence" />
          </HomeCard>
        </BandCards>
      )}
    </section>
  );
}
